import { Box, Typography, Tabs, Tab, Chip, Stack, Collapse, useTheme, CircularProgress } from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MdOutlineKeyboardArrowUp, MdOutlineKeyboardArrowDown } from 'react-icons/md';
import RecommendationCard from './components/RecommendationCard';
import AIRecommendationIcon from '@/assets/icons/AIRecommendationIcon';
import { BuilderEmptyStateView } from '@/components/Common/BuilderEmptyStateView';
import CallLogForm from '../../../CallLogs/components/CallLogForm';
import RejectRecommendationModal from './components/RejectRecommendationModal';
import AddQAForm from './components/AddQAForm';
import {
  useInfiniteGetRecommendations,
  useGetRecommendationsOverview,
  useRejectRecommendation
} from '@/apis/business/agent/performance/hook';
import PendingBadgeLoading from './components/PendingBadgeLoading';
import QATabLoading from './components/QATabLoading';

const TABS = [
  { label: 'All', value: 'all' },
  { label: 'Unanswered Questions', value: 'unanswered' },
  { label: 'Knowledge Gaps', value: 'knowledge' },
  { label: 'Caller Experience', value: 'experience' }
];

export default function AIRecommendationCentre() {
  const theme = useTheme();
  const [tab, setTab] = useState('all');
  const [openQADrawer, setOpenQADrawer] = useState(false);
  const [open, setOpen] = useState(false);
  const [currentItem, setCurrentItem] = useState<any>(null);
  const [callLogDrawerOpen, setCallLogDrawerOpen] = useState(false);
  const [callLogId, setCallLogId] = useState<any>(null);
  const [openRejectModal, setOpenRejectModal] = useState(false);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);

  const { data, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage } = useInfiniteGetRecommendations();
  const { data: overviewData, isLoading: isOverviewLoading } = useGetRecommendationsOverview();
  const { mutateAsync: rejectRecommendation } = useRejectRecommendation();

  const recommendations = useMemo(() => data?.pages.flatMap((page) => page.data) ?? [], [data]);
  const totalCount = data?.pages[0]?.total ?? overviewData?.pending ?? recommendations.length;

  const handleTabChange = (_: any, v: any) => {
    setTab(v);
  };

  const handleObserver = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const [entry] = entries;
      if (entry.isIntersecting && hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    },
    [fetchNextPage, hasNextPage, isFetchingNextPage]
  );

  useEffect(() => {
    const node = loadMoreRef.current;
    if (!node) return;

    const observer = new IntersectionObserver(handleObserver, { root: null, rootMargin: '100px', threshold: 0 });
    observer.observe(node);

    return () => observer.disconnect();
  }, [handleObserver, recommendations.length, open, tab]);

  return (
    <Box
      border={`1px solid ${theme.palette.primary.light}`}
      borderRadius={3}
      bgcolor={theme.palette.primary.light}
      mb={2}
    >
      {/* Header */}
      <Box
        display='flex'
        flexDirection={{ xs: 'column', sm: 'row' }}
        onClick={() => setOpen(!open)}
        justifyContent='space-between'
        alignItems={{ xs: 'stretch', sm: 'center' }}
        gap={{ xs: 1.5, sm: 2 }}
        p={2}
        sx={{ cursor: 'pointer' }}
      >
        <Box display='flex' alignItems='center' gap={2} minWidth={0}>
          <Box
            width={40}
            height={40}
            flexShrink={0}
            borderRadius={2}
            bgcolor='#fff'
            color={theme.palette.primary.main}
            display='flex'
            alignItems='center'
            justifyContent='center'
          >
            <AIRecommendationIcon />
          </Box>

          <Stack minWidth={0}>
            <Typography fontWeight={500} fontSize={16}>
              AI Recommendations Centre
            </Typography>
            <Typography fontSize={12} color='text.secondary'>
              Improve your AI agent based on real call data
            </Typography>
          </Stack>
        </Box>

        <Stack
          direction='row'
          spacing={1}
          alignItems='center'
          useFlexGap
          flexWrap='wrap'
          sx={{
            width: { xs: '100%', sm: 'auto' },
            justifyContent: { xs: 'flex-start', sm: 'flex-end' }
          }}
        >
          {isOverviewLoading ? (
            <>
              <PendingBadgeLoading />
              <PendingBadgeLoading />
              <PendingBadgeLoading />
            </>
          ) : (
            <>
              <StatusBadge
                label='Pending'
                count={overviewData?.pending ?? 0}
                color={theme.palette.error.main}
                bgcolor='#FFF2F2'
              />
              <StatusBadge label='Rejected' count={overviewData?.rejected ?? 0} color='#D97706' bgcolor='#FFFBEB' />
              <StatusBadge
                label='Accepted'
                count={overviewData?.accepted ?? 0}
                color={theme.palette.success.main}
                bgcolor={theme.palette.success.light}
              />
            </>
          )}

          <Box sx={{ cursor: 'pointer', fontSize: 22, flexShrink: 0, ml: { xs: 'auto', sm: 0 } }}>
            {open ? <MdOutlineKeyboardArrowUp /> : <MdOutlineKeyboardArrowDown />}
          </Box>
        </Stack>
      </Box>
      <Collapse in={open}>
        {/* Tabs */}
        <Tabs
          value={tab}
          onChange={handleTabChange}
          variant='scrollable'
          scrollButtons='auto'
          allowScrollButtonsMobile
          sx={{
            px: 2,
            borderBottom: `1px solid ${theme.palette.divider}`,
            minHeight: 44,
            '& .MuiTabs-indicator': {
              height: 3,
              borderRadius: 2
            },
            '& .MuiTab-root': {
              minWidth: 'auto'
            }
          }}
        >
          {TABS.map((t) => (
            <Tab
              key={t.value}
              value={t.value}
              disableRipple
              sx={{
                textTransform: 'none',
                fontSize: 14,
                minHeight: 44,
                fontWeight: 500,
                px: 2,
                color: 'text.secondary',
                '&.Mui-selected': {
                  color: theme.palette.primary.main,
                  borderRadius: 0,
                  borderBottom: `2px solid ${theme.palette.primary.main} `,
                  fontWeight: 600
                }
              }}
              label={
                <Stack direction='row' spacing={1} alignItems='center'>
                  <span>{t.label}</span>

                  {totalCount > 0 && ['all', 'unanswered'].includes(t.value) ? (
                    <Chip
                      label={totalCount}
                      size='small'
                      sx={{
                        height: 20,
                        fontSize: 12,
                        padding: '8px 2px',
                        borderRadius: 50,
                        bgcolor: '#fff',
                        border: `1px solid ${theme.palette.divider}`
                      }}
                    />
                  ) : null}
                </Stack>
              }
            />
          ))}
        </Tabs>

        {isLoading ? (
          <Box p={2}>
            <Stack spacing={2}>
              <QATabLoading />
              <QATabLoading />
            </Stack>
          </Box>
        ) : recommendations.length && ['all', 'unanswered'].includes(tab) ? (
          <Box p={2}>
            <Stack spacing={2}>
              {recommendations.map((item: any) => (
                <RecommendationCard
                  key={item.id}
                  item={item}
                  onReject={(currItem: any) => {
                    setCurrentItem(currItem);
                    setOpenRejectModal(true);
                  }}
                  onOpenCallLog={(id: any) => {
                    setCallLogId(id);
                    setCallLogDrawerOpen(true);
                  }}
                  onAddAnswer={(currItem: any) => {
                    setOpenQADrawer(true);
                    setCurrentItem(currItem);
                  }}
                />
              ))}

              <Box ref={loadMoreRef} display='flex' justifyContent='center' py={1} minHeight={24}>
                {isFetchingNextPage && <CircularProgress size={22} />}
              </Box>
            </Stack>
          </Box>
        ) : (
          <Box border='1px solid #ECECEC' padding='24px' bgcolor='white' m={2} borderRadius='12px'>
            <BuilderEmptyStateView
              iconWidth={250}
              iconHeight={130}
              message={['all', 'unanswered'].includes(tab) ? 'No Data Found' : 'Coming Soon'}
            />
          </Box>
        )}
      </Collapse>
      <AddQAForm
        defaultValues={currentItem}
        open={openQADrawer}
        onClose={() => {
          setOpenQADrawer(false);
          setCurrentItem(null);
        }}
      />
      <CallLogForm logId={callLogId} open={callLogDrawerOpen} onClose={() => setCallLogDrawerOpen(false)} />
      <RejectRecommendationModal
        open={openRejectModal}
        onClose={() => setOpenRejectModal(false)}
        onConfirm={() => {
          rejectRecommendation(currentItem?.id);
          setOpenRejectModal(false);
        }}
      />
    </Box>
  );
}

interface StatusBadgeProps {
  label: string;
  count: number;
  color: string;
  bgcolor: string;
}

function StatusBadge({ label, count, color, bgcolor }: StatusBadgeProps) {
  return (
    <Box
      display='flex'
      flexDirection='row'
      alignItems='center'
      gap={0.5}
      bgcolor={bgcolor}
      borderRadius={12}
      px={{ xs: 1, sm: 1.5 }}
      py='4px'
      flexShrink={0}
      whiteSpace='nowrap'
    >
      <Box width={6} height={6} borderRadius='50%' bgcolor={color} />
      <Typography fontSize={12} fontWeight={400} color={color}>
        {count} {label}
      </Typography>
    </Box>
  );
}
